import React, { useEffect, useState } from 'react';
export function SystemQuote({
  priceType,
  onAddToCart,
  pricingData,
  onPendingItemsChange,
  showPricing
}) {
  const [selectedSystems, setSelectedSystems] = useState([]);
  // Notify parent component when pending items status changes
  useEffect(() => {
    onPendingItemsChange(selectedSystems.length > 0);
  }, [selectedSystems, onPendingItemsChange]);
  const addSystem = type => {
    const newSystem = {
      type,
      id: Date.now(),
      quantity: 1,
      chestGuardSizes: [],
      headGearSizes: []
    };
    // Add default chest guard sizes for systems that include body
    if (type === 'full' || type === 'judgeAndBody' || type === 'bodyOnly') {
      newSystem.chestGuardSizes = [{
        size: 'size2',
        color: 'red',
        quantity: 1
      }, {
        size: 'size2',
        color: 'blue',
        quantity: 1
      }];
    }
    // Add default head gear sizes for full systems
    if (type === 'full') {
      newSystem.headGearSizes = [{
        size: 'M',
        color: 'red',
        quantity: 1
      }, {
        size: 'M',
        color: 'blue',
        quantity: 1
      }];
    }
    // Add new system at the beginning of the array so it appears at the top
    setSelectedSystems([newSystem, ...selectedSystems]);
  };
  const removeSystem = id => {
    setSelectedSystems(selectedSystems.filter(system => system.id !== id));
  };
  const updateSystemQuantity = (id, quantity) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === id) {
        return {
          ...system,
          quantity: parseInt(quantity) || 1
        };
      }
      return system;
    }));
  };
  const addChestGuardSize = (systemId, size, color, quantity) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === systemId) {
        const existingSizeIndex = system.chestGuardSizes.findIndex(item => item.size === size && item.color === color);
        if (existingSizeIndex >= 0) {
          const updatedSizes = [...system.chestGuardSizes];
          updatedSizes[existingSizeIndex] = {
            ...updatedSizes[existingSizeIndex],
            quantity: parseInt(quantity) || 1
          };
          return {
            ...system,
            chestGuardSizes: updatedSizes
          };
        } else {
          return {
            ...system,
            chestGuardSizes: [...system.chestGuardSizes, {
              size,
              color,
              quantity: parseInt(quantity) || 1
            }]
          };
        }
      }
      return system;
    }));
  };
  const removeChestGuardSize = (systemId, size, color) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === systemId) {
        return {
          ...system,
          chestGuardSizes: system.chestGuardSizes.filter(item => !(item.size === size && item.color === color))
        };
      }
      return system;
    }));
  };
  const addHeadGearSize = (systemId, size, color, quantity) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === systemId) {
        const existingSizeIndex = system.headGearSizes.findIndex(item => item.size === size && item.color === color);
        if (existingSizeIndex >= 0) {
          const updatedSizes = [...system.headGearSizes];
          updatedSizes[existingSizeIndex] = {
            ...updatedSizes[existingSizeIndex],
            quantity: parseInt(quantity) || 1
          };
          return {
            ...system,
            headGearSizes: updatedSizes
          };
        } else {
          return {
            ...system,
            headGearSizes: [...system.headGearSizes, {
              size,
              color,
              quantity: parseInt(quantity) || 1
            }]
          };
        }
      }
      return system;
    }));
  };
  const removeHeadGearSize = (systemId, size, color) => {
    setSelectedSystems(selectedSystems.map(system => {
      if (system.id === systemId) {
        return {
          ...system,
          headGearSizes: system.headGearSizes.filter(item => !(item.size === size && item.color === color))
        };
      }
      return system;
    }));
  };
  // Helper function to get head guard price by size and color
  const getHeadGuardPrice = (size, color) => {
    const headGuardId = `headGuard${size}${color.charAt(0).toUpperCase() + color.slice(1)}`;
    const headGuard = pricingData.find(item => item.id === headGuardId);
    return headGuard ? headGuard[priceType] : 399.99;
  };
  const calculateSystemTotal = system => {
    let total = 0;
    const {
      type,
      quantity,
      chestGuardSizes,
      headGearSizes
    } = system;
    // Common items for all systems
    const software = pricingData.find(item => item.id === 'software');
    const receiver = pricingData.find(item => item.id === 'receiver');
    total += software[priceType] * quantity;
    total += receiver[priceType] * quantity;
    if (type === 'full' || type === 'judgeAndBody' || type === 'bodyOnly') {
      const chestTransmitter = pricingData.find(item => item.id === 'chestTransmitter');
      total += chestTransmitter[priceType] * quantity * 2; // 2 transmitters per system
      // Add chest guards based on selected sizes
      chestGuardSizes.forEach(({
        size,
        quantity: sizeQuantity
      }) => {
        // Map individual sizes to the corresponding chest guard product
        const sizeGroup = getSizeGroup(size);
        const sizeKey = `chestGuard${sizeGroup}`;
        const chestGuard = pricingData.find(item => item.id === sizeKey);
        if (chestGuard) {
          total += chestGuard[priceType] * quantity * sizeQuantity;
        }
      });
    }
    if (type === 'full') {
      const headTransmitter = pricingData.find(item => item.id === 'headTransmitter');
      total += headTransmitter[priceType] * quantity * 2; // 2 transmitters per system
      // Add head gear based on selected sizes
      headGearSizes.forEach(({
        size,
        color,
        quantity: sizeQuantity
      }) => {
        const headGuardPrice = getHeadGuardPrice(size, color);
        total += headGuardPrice * quantity * sizeQuantity;
      });
    }
    if (type === 'full' || type === 'judgeAndBody' || type === 'judgeOnly') {
      const judgeBox = pricingData.find(item => item.id === 'judgeBox');
      total += judgeBox[priceType] * quantity * 2; // 2 judge boxes per system
    }
    return total.toFixed(2);
  };
  const handleAddToQuote = () => {
    let cartItems = {};
    selectedSystems.forEach(system => {
      const {
        type,
        quantity,
        headGearSizes,
        chestGuardSizes
      } = system;
      // Common items for all systems
      if (!cartItems['software']) cartItems['software'] = 0;
      cartItems['software'] += quantity;
      if (!cartItems['receiver']) cartItems['receiver'] = 0;
      cartItems['receiver'] += quantity;
      if (type === 'full') {
        // Full Ring
        if (!cartItems['chestTransmitter']) cartItems['chestTransmitter'] = 0;
        cartItems['chestTransmitter'] += quantity * 2;
        if (!cartItems['headTransmitter']) cartItems['headTransmitter'] = 0;
        cartItems['headTransmitter'] += quantity * 2;
        if (!cartItems['judgeBox']) cartItems['judgeBox'] = 0;
        cartItems['judgeBox'] += quantity * 2;
        // Add head gears based on selected sizes
        headGearSizes.forEach(({
          size,
          color,
          quantity: sizeQuantity
        }) => {
          // Map to individual head guard items
          const headGearId = `headGuard${size}${color.charAt(0).toUpperCase() + color.slice(1)}`;
          if (!cartItems[headGearId]) cartItems[headGearId] = 0;
          cartItems[headGearId] += quantity * sizeQuantity;
        });
        // Add chest guards based on selected sizes
        chestGuardSizes.forEach(({
          size,
          color,
          quantity: sizeQuantity
        }) => {
          // Map to individual chest guard items
          const sizeNumber = size.replace('size', '');
          const chestGuardId = `chestGuardSize${sizeNumber}${color.charAt(0).toUpperCase() + color.slice(1)}`;
          if (!cartItems[chestGuardId]) cartItems[chestGuardId] = 0;
          cartItems[chestGuardId] += quantity * sizeQuantity;
        });
      } else if (type === 'judgeAndBody') {
        // Judge and Body
        if (!cartItems['chestTransmitter']) cartItems['chestTransmitter'] = 0;
        cartItems['chestTransmitter'] += quantity * 2;
        if (!cartItems['judgeBox']) cartItems['judgeBox'] = 0;
        cartItems['judgeBox'] += quantity * 2;
        // Add chest guards based on selected sizes
        chestGuardSizes.forEach(({
          size,
          color,
          quantity: sizeQuantity
        }) => {
          // Map to individual chest guard items
          const sizeNumber = size.replace('size', '');
          const chestGuardId = `chestGuardSize${sizeNumber}${color.charAt(0).toUpperCase() + color.slice(1)}`;
          if (!cartItems[chestGuardId]) cartItems[chestGuardId] = 0;
          cartItems[chestGuardId] += quantity * sizeQuantity;
        });
      } else if (type === 'bodyOnly') {
        // Body Only
        if (!cartItems['chestTransmitter']) cartItems['chestTransmitter'] = 0;
        cartItems['chestTransmitter'] += quantity * 2;
        // Add chest guards based on selected sizes
        chestGuardSizes.forEach(({
          size,
          color,
          quantity: sizeQuantity
        }) => {
          // Map to individual chest guard items
          const sizeNumber = size.replace('size', '');
          const chestGuardId = `chestGuardSize${sizeNumber}${color.charAt(0).toUpperCase() + color.slice(1)}`;
          if (!cartItems[chestGuardId]) cartItems[chestGuardId] = 0;
          cartItems[chestGuardId] += quantity * sizeQuantity;
        });
      } else if (type === 'judgeOnly') {
        // Judge Only
        if (!cartItems['judgeBox']) cartItems['judgeBox'] = 0;
        cartItems['judgeBox'] += quantity * 2;
      }
    });
    onAddToCart(cartItems);
    setSelectedSystems([]);
  };
  // Helper function to map individual size to size group for pricing
  const getSizeGroup = size => {
    if (size === 'size0' || size === 'size1') {
      return 'size01';
    } else if (size === 'size2' || size === 'size3') {
      return 'size23';
    } else if (size === 'size4' || size === 'size5') {
      return 'size45';
    }
    return size;
  };
  const getChestGuardPrice = size => {
    // Map individual sizes to their price groups
    const sizeGroup = getSizeGroup(size);
    if (sizeGroup === 'size01') {
      return priceType === 'wholesale' ? 706.01 : 847.22;
    } else if (sizeGroup === 'size23') {
      return priceType === 'wholesale' ? 726.29 : 871.55;
    } else if (sizeGroup === 'size45') {
      return priceType === 'wholesale' ? 748.55 : 898.26;
    }
    return 0;
  };
  const getChestGuardSizeText = sizeCode => {
    switch (sizeCode) {
      case 'size0':
        return 'Size 0';
      case 'size1':
        return 'Size 1';
      case 'size2':
        return 'Size 2';
      case 'size3':
        return 'Size 3';
      case 'size4':
        return 'Size 4';
      case 'size5':
        return 'Size 5';
      default:
        return sizeCode;
    }
  };
  // Check if there's an imbalance in red/blue chest guards of the same size
  const checkColorImbalance = chestGuardSizes => {
    const sizeColorCount = {};
    // Count quantities by size and color
    chestGuardSizes.forEach(({
      size,
      color,
      quantity
    }) => {
      if (!sizeColorCount[size]) {
        sizeColorCount[size] = {
          red: 0,
          blue: 0
        };
      }
      sizeColorCount[size][color] += quantity;
    });
    // Check for imbalances
    for (const size in sizeColorCount) {
      const {
        red,
        blue
      } = sizeColorCount[size];
      if (red !== blue) {
        return true;
      }
    }
    return false;
  };
  // Check if there's an imbalance in red/blue head gears of the same size
  const checkHeadGearColorImbalance = headGearSizes => {
    const sizeColorCount = {};
    // Count quantities by size and color
    headGearSizes.forEach(({
      size,
      color,
      quantity
    }) => {
      if (!sizeColorCount[size]) {
        sizeColorCount[size] = {
          red: 0,
          blue: 0
        };
      }
      sizeColorCount[size][color] += quantity;
    });
    // Check for imbalances
    for (const size in sizeColorCount) {
      const {
        red,
        blue
      } = sizeColorCount[size];
      if (red !== blue) {
        return true;
      }
    }
    return false;
  };
  return <div className="mb-8" data-id="element-229">
      <h2 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-230">
        System Configuration
      </h2>
      <div className="flex flex-wrap gap-4 mb-6" data-id="element-231">
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => addSystem('full')} data-id="element-232">
          Add Full Ring
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => addSystem('judgeAndBody')} data-id="element-233">
          Add Judge and Body
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => addSystem('bodyOnly')} data-id="element-234">
          Add Body Only
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => addSystem('judgeOnly')} data-id="element-235">
          Add Judge Only
        </button>
      </div>
      {selectedSystems.length > 0 && <div className="space-y-6" data-id="element-236">
          {selectedSystems.map((system, index) => <div key={system.id} className="border rounded-lg p-4 bg-gray-50" data-id="element-237">
              <div className="flex justify-between items-center mb-4" data-id="element-238">
                <div className="flex items-center" data-id="element-239">
                  <h3 className="text-xl font-semibold mr-3" data-id="element-240">
                    {system.type === 'full' && 'Full Ring'}
                    {system.type === 'judgeAndBody' && 'Judge and Body'}
                    {system.type === 'bodyOnly' && 'Body Only'}
                    {system.type === 'judgeOnly' && 'Judge Only'}
                  </h3>
                </div>
                <button className="text-red-500 hover:text-red-700" onClick={() => removeSystem(system.id)} data-id="element-241">
                  Remove
                </button>
              </div>
              <div className="mb-4" data-id="element-242">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-243">
                  Quantity:
                </label>
                <input type="number" min="1" value={system.quantity} onChange={e => updateSystemQuantity(system.id, e.target.value)} className="border rounded-md px-3 py-2 w-24" data-id="element-244" />
              </div>
              {/* Head Gear Sizes Section for Full Ring */}
              {system.type === 'full' && <div className="mb-4" data-id="element-245">
                  <h4 className="font-medium mb-2" data-id="element-246">Head Gear Sizes:</h4>
                  <div className="space-y-2" data-id="element-247">
                    {system.headGearSizes.map((item, sizeIndex) => <div key={sizeIndex} className="flex items-center space-x-4" data-id="element-248">
                        <div className="w-32 flex items-center" data-id="element-249">
                          <div className={`w-4 h-4 rounded-full mr-2 ${item.color === 'red' ? 'bg-red-600' : 'bg-blue-600'}`} data-id="element-250"></div>
                          Size {item.size} ({item.color})
                        </div>
                        <input type="number" min="1" value={item.quantity} onChange={e => addHeadGearSize(system.id, item.size, item.color, e.target.value)} className="border rounded-md px-3 py-2 w-24" data-id="element-251" />
                        <button className="text-red-500 hover:text-red-700" onClick={() => removeHeadGearSize(system.id, item.size, item.color)} data-id="element-252">
                          Remove
                        </button>
                      </div>)}
                    {system.headGearSizes.length > 0 && checkHeadGearColorImbalance(system.headGearSizes) && <div className="text-red-500 text-sm mt-2" data-id="element-253">
                          Warning: In a real competition, both red and blue
                          players must wear the same size head gear. Please
                          ensure you have an equal number of red and blue head
                          gears for each size.
                        </div>}
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-254">
                      <div className="w-full font-medium text-sm mb-1" data-id="element-255">
                        Add Red Head Gear:
                      </div>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addHeadGearSize(system.id, 'XS', 'red', 1)} data-id="element-256">
                        Size XS (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addHeadGearSize(system.id, 'S', 'red', 1)} data-id="element-257">
                        Size S (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addHeadGearSize(system.id, 'M', 'red', 1)} data-id="element-258">
                        Size M (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addHeadGearSize(system.id, 'L', 'red', 1)} data-id="element-259">
                        Size L (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addHeadGearSize(system.id, 'XL', 'red', 1)} data-id="element-260">
                        Size XL (Red)
                      </button>
                      <div className="w-full font-medium text-sm mb-1 mt-2" data-id="element-261">
                        Add Blue Head Gear:
                      </div>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addHeadGearSize(system.id, 'XS', 'blue', 1)} data-id="element-262">
                        Size XS (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addHeadGearSize(system.id, 'S', 'blue', 1)} data-id="element-263">
                        Size S (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addHeadGearSize(system.id, 'M', 'blue', 1)} data-id="element-264">
                        Size M (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addHeadGearSize(system.id, 'L', 'blue', 1)} data-id="element-265">
                        Size L (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addHeadGearSize(system.id, 'XL', 'blue', 1)} data-id="element-266">
                        Size XL (Blue)
                      </button>
                    </div>
                  </div>
                </div>}
              {/* Chest Guard Sizes Section */}
              {(system.type === 'full' || system.type === 'judgeAndBody' || system.type === 'bodyOnly') && <div className="mb-4" data-id="element-267">
                  <h4 className="font-medium mb-2" data-id="element-268">Chest Guard Sizes:</h4>
                  <div className="space-y-2" data-id="element-269">
                    {system.chestGuardSizes.map((item, sizeIndex) => <div key={sizeIndex} className="flex items-center space-x-4" data-id="element-270">
                        <div className="w-32 flex items-center" data-id="element-271">
                          <div className={`w-4 h-4 rounded-full mr-2 ${item.color === 'red' ? 'bg-red-600' : 'bg-blue-600'}`} data-id="element-272"></div>
                          {getChestGuardSizeText(item.size)} ({item.color})
                        </div>
                        <input type="number" min="1" value={item.quantity} onChange={e => addChestGuardSize(system.id, item.size, item.color, e.target.value)} className="border rounded-md px-3 py-2 w-24" data-id="element-273" />
                        <button className="text-red-500 hover:text-red-700" onClick={() => removeChestGuardSize(system.id, item.size, item.color)} data-id="element-274">
                          Remove
                        </button>
                      </div>)}
                    {system.chestGuardSizes.length > 0 && checkColorImbalance(system.chestGuardSizes) && <div className="text-red-500 text-sm mt-2" data-id="element-275">
                          Warning: In a real competition, both red and blue
                          players must wear the same size chest guard. Please
                          ensure you have an equal number of red and blue chest
                          guards for each size.
                        </div>}
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-276">
                      <div className="w-full font-medium text-sm mb-1" data-id="element-277">
                        Add Red Chest Guards:
                      </div>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addChestGuardSize(system.id, 'size0', 'red', 1)} data-id="element-278">
                        Size 0 (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addChestGuardSize(system.id, 'size1', 'red', 1)} data-id="element-279">
                        Size 1 (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addChestGuardSize(system.id, 'size2', 'red', 1)} data-id="element-280">
                        Size 2 (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addChestGuardSize(system.id, 'size3', 'red', 1)} data-id="element-281">
                        Size 3 (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addChestGuardSize(system.id, 'size4', 'red', 1)} data-id="element-282">
                        Size 4 (Red)
                      </button>
                      <button className="px-3 py-1 bg-red-100 text-gray-800 rounded-md text-sm hover:bg-red-200" onClick={() => addChestGuardSize(system.id, 'size5', 'red', 1)} data-id="element-283">
                        Size 5 (Red)
                      </button>
                      <div className="w-full font-medium text-sm mb-1 mt-2" data-id="element-284">
                        Add Blue Chest Guards:
                      </div>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addChestGuardSize(system.id, 'size0', 'blue', 1)} data-id="element-285">
                        Size 0 (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addChestGuardSize(system.id, 'size1', 'blue', 1)} data-id="element-286">
                        Size 1 (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addChestGuardSize(system.id, 'size2', 'blue', 1)} data-id="element-287">
                        Size 2 (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addChestGuardSize(system.id, 'size3', 'blue', 1)} data-id="element-288">
                        Size 3 (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addChestGuardSize(system.id, 'size4', 'blue', 1)} data-id="element-289">
                        Size 4 (Blue)
                      </button>
                      <button className="px-3 py-1 bg-blue-100 text-gray-800 rounded-md text-sm hover:bg-blue-200" onClick={() => addChestGuardSize(system.id, 'size5', 'blue', 1)} data-id="element-290">
                        Size 5 (Blue)
                      </button>
                    </div>
                  </div>
                </div>}
              <div className="text-sm text-gray-600" data-id="element-291">
                <h4 className="font-medium mb-1" data-id="element-292">
                  Included in this configuration:
                </h4>
                <ul className="list-disc pl-5" data-id="element-293">
                  <li data-id="element-294">Gen 3 Software</li>
                  <li data-id="element-295">Receiver</li>
                  {(system.type === 'full' || system.type === 'judgeAndBody' || system.type === 'bodyOnly') && <li data-id="element-296">2 Gen 3 Chest Guard Transmitters</li>}
                  {system.type === 'full' && <li data-id="element-297">2 Gen 3 Head Transmitters</li>}
                  {(system.type === 'full' || system.type === 'judgeOnly' || system.type === 'judgeAndBody') && <li data-id="element-298">2 Judge Boxes</li>}
                  {system.type === 'full' && <li data-id="element-299">Gen 3 Head Guards</li>}
                </ul>
              </div>
            </div>)}
          <div className="mt-6" data-id="element-300">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={handleAddToQuote} data-id="element-301">
              Add to Quote
            </button>
          </div>
        </div>}
    </div>;
}